import React, {useState, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useGetLogisticListQuery} from '../../../../redux/DepotSlice/depotCashierApi';
import ReactSelectStyled, {
	BaseOption,
} from '../../../../Components/ReactSelectStyled/ReactSelectStyled';
import s from './SupplierSelector.module.css';

type SupplierSelectorProps = {
	onSupplierChange?: (supplierId: number | null) => void;
	initialSupplierId?: string;
	orderType?: 'wholesale' | 'retail'; // Тип заказа для автоматического выбора ФОП
};

const SupplierSelector: React.FC<SupplierSelectorProps> = ({
	onSupplierChange,
	initialSupplierId,
	orderType = 'wholesale',
}) => {
	const {t} = useTranslation();
	const {data: suppliersList, isLoading} = useGetLogisticListQuery();
	const [selectedSupplier, setSelectedSupplier] = useState<BaseOption | null>(
		null,
	);
	const isInitialized = useRef(false);

	// Преобразуем список поставщиков в опции для селекта
	const supplierOptions: BaseOption[] =
		suppliersList?.suppliers?.map((supplier) => ({
			label: supplier.supplier_name,
			value: supplier.supplier_id,
		})) || [];

	// Инициализация при редактировании
	useEffect(() => {
		if (
			initialSupplierId &&
			suppliersList?.suppliers &&
			!isInitialized.current
		) {
			const initialSupplier = suppliersList.suppliers.find(
				(s) => s.supplier_id === parseInt(initialSupplierId),
			);
			if (initialSupplier) {
				setSelectedSupplier({
					label: initialSupplier.supplier_name,
					value: initialSupplier.supplier_id,
				});
				isInitialized.current = true;
			}
		}
	}, [initialSupplierId, suppliersList]);

	// Автоматический выбор ФОП при изменении типа заказа
	useEffect(() => {
		// Пропускаем, если уже инициализирован (при редактировании)
		if (isInitialized.current && initialSupplierId) {
			return;
		}

		if (suppliersList?.suppliers && suppliersList.suppliers.length > 0) {
			// Если есть несколько ФОП, выбираем в зависимости от типа заказа
			if (suppliersList.suppliers.length >= 2) {
				// Первый ФОП для опта (wholesale), второй для розницы (retail)
				const supplierIndex = orderType === 'retail' ? 1 : 0;
				const selectedSupplierData = suppliersList.suppliers[supplierIndex];
				
				setSelectedSupplier({
					label: selectedSupplierData.supplier_name,
					value: selectedSupplierData.supplier_id,
				});
			} else if (suppliersList.suppliers.length === 1 && !selectedSupplier) {
				// Если только один ФОП, выбираем его по умолчанию
				const supplierData = suppliersList.suppliers[0];
				setSelectedSupplier({
					label: supplierData.supplier_name,
					value: supplierData.supplier_id,
				});
			}
			
			if (!isInitialized.current) {
				isInitialized.current = true;
			}
		}
	}, [orderType, suppliersList, initialSupplierId]);

	// Уведомляем родительский компонент об изменении
	useEffect(() => {
		if (onSupplierChange) {
			onSupplierChange(
				selectedSupplier ? (selectedSupplier.value as number) : null,
			);
		}
	}, [selectedSupplier, onSupplierChange]);

	// Если нет фискализации, не показываем селектор
	if (!suppliersList?.suppliers || suppliersList.suppliers.length === 0) {
		return null;
	}

	if (isLoading) {
		return (
			<div className={s.supplierSelectorContainer}>
				<div className={s.title}>{t('Завантаження ФОП...')}</div>
			</div>
		);
	}

	return (
		<div className={s.supplierSelectorContainer}>
			<div className={s.title}>{t('Вибір ФОП (фіскалізація)')}</div>
			<div className={s.selectWrapper}>
				<ReactSelectStyled
					value={selectedSupplier}
					setValue={setSelectedSupplier}
					options={supplierOptions}
					placeholder={t('Оберіть ФОП')}
					fontSize={16}
					height={50}
				/>
			</div>
			{selectedSupplier && (
				<div className={s.selectedSupplierInfo}>
					<div className={s.supplierName}>
						{t('Обрано ФОП')}: <strong>{selectedSupplier.label}</strong>
					</div>
					<div className={s.orderTypeInfo}>
						{t('Тип замовлення')}: <strong>{orderType === 'retail' ? t('Роздріб') : t('Опт')}</strong>
					</div>
					<div className={s.infoText}>
						{t(
							'Оформлення буде зареєстровано з фіскалізацією через цього ФОП',
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default SupplierSelector;

